import { useEffect, useState } from "react";
import {
  ShoppingBag,
  Clock,
  CheckCircle,
  IndianRupee,
  Building2,
  Pencil,
  Check,
  X,
} from "lucide-react";

import { getMyOrders, GetSettings, UpdateSettings } from "../api";

function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [gstNo, setGstNo] = useState("");
  const [tempGstNo, setTempGstNo] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadOrders();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await GetSettings();

      if (data.success) {
        const gst = data.settings?.gstNumber || "";

        setGstNo(gst);
        setTempGstNo(gst);

        setCompanyName(data.settings?.companyName || "Polybionic Technologies");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSaveGST = async () => {
    try {
      const { data } = await UpdateSettings({
        gstNumber: tempGstNo,
      });

      if (data.success) {
        setGstNo(tempGstNo);
        setIsEditing(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancel = () => {
    setTempGstNo(gstNo);
    setIsEditing(false);
  };

  const loadOrders = async () => {
    try {
      const { data } = await getMyOrders();

      if (data.success) {
        setOrders(data.orders);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const totalOrders = orders.length;

  const pendingOrders = orders.filter(
    (order) => order.status === "Processing",
  ).length;

  const deliveredOrders = orders.filter(
    (order) => order.status === "Delivered",
  ).length;

  const totalSpent = orders.reduce(
    (sum, order) => sum + Number(order.amount || 0),
    0,
  );

  const stats = [
    {
      title: "Total Orders",
      value: totalOrders,
      icon: <ShoppingBag size={28} />,
      color: "bg-blue-500",
    },
    {
      title: "Pending Orders",
      value: pendingOrders,
      icon: <Clock size={28} />,
      color: "bg-yellow-500",
    },
    {
      title: "Delivered Orders",
      value: deliveredOrders,
      icon: <CheckCircle size={28} />,
      color: "bg-green-500",
    },
    {
      title: "Total Spent",
      value: `₹${totalSpent}`,
      icon: <IndianRupee size={28} />,
      color: "bg-purple-500",
    },
  ];

  if (loading) {
    return (
      <div className="p-6">
        {" "}
        <h2 className="text-xl font-semibold">Loading Dashboard... </h2>{" "}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}{" "}
      <div className="md:mb-8 mb-4 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        {/* Heading */}
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
            Buyer Dashboard
          </h1>

          <p className="text-gray-500 mt-2 text-sm sm:text-base">
            Manage your orders and purchases
          </p>
        </div>

        {/* GST Card */}
        <div className="w-full sm:w-[320px] bg-white border border-green-200 rounded-xl p-4 shadow-sm">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">GST No</p>

            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <Pencil size={16} className="text-green-600" />
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSaveGST}
                  className="p-1 rounded hover:bg-green-100"
                >
                  <Check size={18} className="text-green-600" />
                </button>

                <button
                  onClick={handleCancel}
                  className="p-1 rounded hover:bg-red-100"
                >
                  <X size={18} className="text-red-600" />
                </button>
              </div>
            )}
          </div>

          {!isEditing ? (
            <h3 className="mt-0 md:mt-2 text-lg sm:text-xl font-bold break-all">
              {gstNo || "N/A"}
            </h3>
          ) : (
            <input
              type="text"
              value={tempGstNo}
              onChange={(e) => setTempGstNo(e.target.value)}
              placeholder="Enter GST Number"
              className="w-full mt-3 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          )}
        </div>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-6">
        {stats.map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl shadow-sm border p-3 md:p-6"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-500 text-sm">{item.title}</p>

                <h2 className="text-3xl font-bold md:mt-2 mt-0">{item.value}</h2>
              </div>

              <div className={`${item.color} text-white p-1 md:p-4 rounded-xl`}>
                {item.icon}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Recent Orders */}
      <div className="bg-white rounded-2xl shadow-sm border mt-4 md:mt-8 p-4 md:p-6">
        <h2 className="text-xl md:text-2xl font-semibold mb-5">
          Recent Orders
        </h2>

        {orders.length === 0 ? (
          <p className="text-gray-500">No orders found</p>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-4">Order ID</th>
                    <th className="text-left">Product</th>
                    <th className="text-left">Amount</th>
                    <th className="text-left">Status</th>
                  </tr>
                </thead>

                <tbody>
                  {orders.slice(0, 5).map((order) => (
                    <tr key={order._id} className="border-b last:border-0">
                      <td className="py-4 font-medium">{order.orderId}</td>

                      <td>
                        {order.items
                          ?.map((item) => item.title || item.name)
                          .join(", ")}
                      </td>

                      <td className="font-semibold text-green-600">
                        ₹{order.amount}
                      </td>

                      <td>
                        <span
                          className={`px-3 py-1 rounded-full text-sm ${
                            order.status === "Delivered"
                              ? "bg-green-100 text-green-700"
                              : order.status === "Shipped"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="space-y-4 md:hidden">
              {orders.slice(0, 5).map((order) => (
                <div
                  key={order._id}
                  className="border rounded-xl p-4 shadow-sm"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-xs text-gray-500">Order ID</p>
                      <p className="font-semibold break-all">{order.orderId}</p>
                    </div>

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        order.status === "Delivered"
                          ? "bg-green-100 text-green-700"
                          : order.status === "Shipped"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {order.status}
                    </span>
                  </div>

                  <div className="mb-3">
                    <p className="text-xs text-gray-500">Products</p>
                    <p className="text-sm">
                      {order.items
                        ?.map((item) => item.title || item.name)
                        .join(", ")}
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Amount</span>
                    <span className="font-bold text-green-600">
                      ₹{order.amount}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
